import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { ROLES, type Role } from "@/app/marketing/constants/rolesCatalog";

const VIDEO_BY_CODE: Record<string, string> = {
  "ticket-responder": "/agents/ticket-responder.webm",
  "call-center-agent": "/agents/call-center-agent.webm",
  "executive-assistant": "/agents/executive-assistant.webm",
  "data-analyst": "/agents/data-analyst.webm",
  "bi-developer": "/agents/bi-developer.webm",
  "growth-marketer": "/agents/growth-marketer.webm",
  "ad-campaign-manager": "/agents/ad-campaign-manager.webm",
};

const STORY_DURATION_MS = 5000;
const GAP_PX = 16;
const ANIMATED_ROLES: Role[] = ROLES.filter((r) => Boolean(VIDEO_BY_CODE[r.code]));
const N = ANIMATED_ROLES.length;
const STRIP: Role[] = [...ANIMATED_ROLES, ...ANIMATED_ROLES, ...ANIMATED_ROLES];

// Responsive: how many cards visible at the current viewport width.
function getVisibleCount(width: number): number {
  if (width < 640) return 1; // mobile
  if (width < 1024) return 2; // tablet
  return 3; // desktop
}

// Slot the active card occupies — middle on desktop, leftmost otherwise.
function getActiveSlot(visibleCount: number): number {
  return Math.floor((visibleCount - 1) / 2);
}

// Instagram-story-style 3-card carousel with responsive viewport.
export function AgentMarqueeCarousel() {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);

  // logicalIndex points into STRIP. Start at N (middle copy) so the snap-back
  // logic keeps us safely away from either edge.
  const [logicalIndex, setLogicalIndex] = useState(N);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [noAnim, setNoAnim] = useState(false);
  const startRef = useRef<number>(performance.now());

  const activeRoleIndex = logicalIndex % N;

  // Measure viewport with ResizeObserver
  useLayoutEffect(() => {
    if (!viewportRef.current) return;
    const el = viewportRef.current;
    const ro = new ResizeObserver(([entry]) => {
      setViewportWidth(entry.contentRect.width);
    });
    ro.observe(el);
    setViewportWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  // 5-second story timer
  useEffect(() => {
    if (paused) return;
    let frameId = 0;
    startRef.current = performance.now();
    setProgress(0);
    const tick = (now: number) => {
      const elapsed = (now - startRef.current) / STORY_DURATION_MS;
      if (elapsed >= 1) {
        setLogicalIndex((i) => i + 1);
      } else {
        setProgress(elapsed);
        frameId = requestAnimationFrame(tick);
      }
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, logicalIndex]);

  // Snap back to the middle copy when drifting toward an edge of the strip.
  useEffect(() => {
    if (logicalIndex >= 2 * N || logicalIndex < N) {
      const id = window.setTimeout(() => {
        setNoAnim(true);
        setLogicalIndex(N + (((logicalIndex % N) + N) % N));
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setNoAnim(false))
        );
      }, 600);
      return () => window.clearTimeout(id);
    }
  }, [logicalIndex]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") setLogicalIndex((i) => i + 1);
      else if (e.key === "ArrowLeft") setLogicalIndex((i) => i - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function jumpToRole(targetRoleIndex: number) {
    setLogicalIndex(N + targetRoleIndex);
  }

  const visibleCount = getVisibleCount(viewportWidth);
  const activeSlot = getActiveSlot(visibleCount);
  const cardWidth =
    viewportWidth > 0
      ? (viewportWidth - (visibleCount - 1) * GAP_PX) / visibleCount
      : 0;
  const step = cardWidth + GAP_PX;
  // Translate the strip so logicalIndex sits in slot `activeSlot`.
  // strip card i is at left = i * step; we want it at activeSlot * step.
  // → translateX = activeSlot * step - logicalIndex * step
  const translateX =
    cardWidth > 0 ? activeSlot * step - logicalIndex * step : 0;

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="Agent catalog story carousel"
    >
      {/* Progress bars — one per role */}
      <div className="flex gap-1.5 mb-6">
        {ANIMATED_ROLES.map((r, i) => (
          <button
            key={r.code}
            onClick={() => jumpToRole(i)}
            className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet"
            aria-label={`Go to ${r.name}`}
          >
            <div
              className="h-full bg-brand-violet rounded-full"
              style={{
                width:
                  i < activeRoleIndex
                    ? "100%"
                    : i === activeRoleIndex
                    ? `${Math.min(progress * 100, 100)}%`
                    : "0%",
                opacity: i < activeRoleIndex ? 0.5 : 1,
                transition:
                  i === activeRoleIndex ? "none" : "width 0.2s ease-out",
              }}
            />
          </button>
        ))}
      </div>

      <div className="relative">
        {/* Viewport */}
        <div ref={viewportRef} className="overflow-hidden w-full">
          <div
            className="flex"
            style={{
              gap: `${GAP_PX}px`,
              transform: `translateX(${translateX}px)`,
              transition: noAnim ? "none" : "transform 500ms ease-out",
              willChange: "transform",
            }}
          >
            {STRIP.map((role, i) => {
              const isActive = i === logicalIndex;
              const videoSrc = VIDEO_BY_CODE[role.code];
              return (
                <article
                  key={`${role.code}-${i}`}
                  style={{
                    flex: `0 0 ${cardWidth}px`,
                    width: `${cardWidth}px`,
                  }}
                  className={[
                    "rounded-2xl border bg-white/5 overflow-hidden transition-all duration-500",
                    isActive
                      ? "border-brand-violet/60 opacity-100 scale-100 shadow-[0_0_40px_-10px_rgba(124,58,237,0.4)]"
                      : "border-white/10 opacity-50 scale-95",
                  ].join(" ")}
                  aria-label={role.name}
                  aria-hidden={!isActive}
                >
                  <div className="relative w-full aspect-[8/5] bg-brand-midnight">
                    {videoSrc ? (
                      <video
                        className="absolute inset-0 h-full w-full object-cover"
                        src={videoSrc}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        aria-hidden
                      />
                    ) : null}
                  </div>
                  <div className="p-5 flex flex-col gap-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-brand-cyan font-mono">
                      {role.category}
                    </div>
                    <h3 className="font-display text-2xl tracking-wide text-brand-ink uppercase leading-none">
                      {role.name}
                    </h3>
                    <p className="text-sm text-brand-ink/70 line-clamp-2">
                      {role.summary}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-base font-mono text-brand-cyan">
                        {role.startingPrice}
                        <span className="text-xs text-brand-ink/60 font-sans">
                          {" "}
                          /mo
                        </span>
                      </span>
                      <Link
                        to={`/roles/${role.code}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-brand-violet hover:text-brand-violet/80"
                      >
                        Learn more
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        {/* Prev / Next */}
        <button
          onClick={() => setLogicalIndex((i) => i - 1)}
          className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center text-brand-ink/80 hover:bg-black/80 hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet z-10"
          aria-label="Previous agent"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={() => setLogicalIndex((i) => i + 1)}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center text-brand-ink/80 hover:bg-black/80 hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet z-10"
          aria-label="Next agent"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
